import React, { useState, useRef, useEffect } from 'react';
import { Menu, X, Phone, Mail, MapPin, Home, Building2, Compass, Sparkles, Layout, MessageCircle } from 'lucide-react';

const EmeraldGardenWebsite = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('home');

    const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbx-hsGBy3-tkmaLLAPJTcFtzfzQfB67CVpS_f8_vkex2n2oy1hupPIFzUs5aG0HbjqQyw/exec';

    function ContactForm({ context = 'Gửi liên hệ' }) {
        const formRef = useRef(null);
        const [isSending, setIsSending] = useState(false);
        const [contactErrors, setContactErrors] = useState({});
        const [alert, setAlert] = useState({ type: '', message: '' });

        const handleSubmitFetch = async (data) => {
            try {
                await fetch(SCRIPT_URL, {
                    method: 'POST',
                    mode: 'no-cors',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(data)
                });
                console.log('Request sent successfully (no-cors mode)');
                return { result: "success", mode: "no-cors" };
            } catch (error) {
                console.error('Fetch error:', error);
                throw error;
            }
        };

        const handleSubmitGet = async (data) => {
            const params = new URLSearchParams(data);
            const fullUrl = `${SCRIPT_URL}?${params.toString()}`;

            try {
                await fetch(fullUrl, {
                    method: 'GET',
                    mode: 'no-cors'
                });
                console.log('GET request sent successfully');
                return { result: "success", mode: "get_request" };
            } catch (error) {
                console.error('GET request error:', error);
                throw error;
            }
        };

        const handleSubmitForm = (data) => {
            return new Promise((resolve) => {
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = SCRIPT_URL;
                form.target = '_blank';
                form.style.display = 'none';

                Object.keys(data).forEach(key => {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = data[key];
                    form.appendChild(input);
                });

                document.body.appendChild(form);
                form.submit();
                document.body.removeChild(form);

                resolve({ result: "success", mode: "form_submit" });
            });
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (!formRef.current) return;
            setIsSending(true);
            setAlert({ type: '', message: '' });

            const formData = new FormData(formRef.current);
            const name = formData.get('name') || '';
            const phone = formData.get('phone') || '';
            const email = formData.get('email') || '';

            if (!phone && !email) {
                setContactErrors({
                    user_phone: 'Vui lòng nhập ít nhất Email hoặc Số điện thoại!',
                    user_email: 'Vui lòng nhập ít nhất Email hoặc Số điện thoại!'
                });
                setIsSending(false);
                return;
            }

            if (!name) {
                setContactErrors({
                    user_name: 'Vui lòng nhập Họ và tên!'
                });
                setIsSending(false);
                return;
            }

            setContactErrors({});

            const scriptData = {
                name,
                phone,
                email,
                time: new Date().toLocaleString(),
            };

            try {
                try {
                    await handleSubmitFetch(scriptData);
                    console.log('Success with fetch no-cors');
                } catch (fetchError) {
                    console.log('Fetch failed, trying GET request...');
                    try {
                        await handleSubmitGet(scriptData);
                        console.log('Success with GET request');
                    } catch (getError) {
                        console.log('GET failed, using form submission...');
                        await handleSubmitForm(scriptData);
                        console.log('Success with form submission');
                    }
                }

                setAlert({
                    type: 'success',
                    message: 'Gửi thành công. Cảm ơn bạn!'
                });

                if (formRef.current) {
                    formRef.current.reset();
                }

            } catch (error) {
                console.error('All submission methods failed:', error);
                setAlert({
                    type: 'error',
                    message: 'Gửi thất bại. Vui lòng thử lại sau!'
                });
            } finally {
                setIsSending(false);
                setTimeout(() => setAlert({ type: '', message: '' }), 5000);
            }
        };

        return (
            <form ref={formRef} onSubmit={handleSubmit} className="bg-white/10 backdrop-blur p-8 rounded-2xl">
                <div className="space-y-4">
                    <input
                        name="name"
                        type="text"
                        placeholder="Họ và tên *"
                        className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-emerald-200 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    />
                    <input
                        name="phone"
                        type="tel"
                        placeholder="Số điện thoại *"
                        className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-emerald-200 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    />
                    <input
                        name="email"
                        type="email"
                        placeholder="Email"
                        className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-emerald-200 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    />
                    <button
                        type="submit"
                        disabled={isSending}
                        className="w-full py-4 bg-gradient-to-r from-yellow-400 to-amber-500 text-gray-900 font-bold rounded-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 disabled:opacity-60"
                    >
                        {isSending ? 'Đang gửi...' : 'Gửi thông tin'}
                    </button>
                </div>

                {contactErrors.user_name && <div className="mt-4 p-3 bg-red-500 text-white rounded">{contactErrors.user_name}</div>}
                {(contactErrors.user_phone || contactErrors.user_email) && <div className="mt-4 p-3 bg-red-500 text-white rounded">{contactErrors.user_phone || contactErrors.user_email}</div>}

                {alert.message && (
                    <div className={`mt-4 p-4 rounded-lg text-center font-semibold ${alert.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                        {alert.message}
                    </div>
                )}
            </form>
        );
    }
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);

            const sections = ['home', 'about', 'location', 'amenities', 'layouts', 'contact'];
            const current = sections.find(section => {
                const element = document.getElementById(section);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    return rect.top <= 100 && rect.bottom >= 100;
                }
                return false;
            });
            if (current) setActiveSection(current);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setMobileMenuOpen(false);
        }
    };

    const amenities = [
        { img: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=600', title: 'Hồ bơi vô cực', desc: 'Hồ bơi cao cấp với tầm nhìn panorama' },
        { img: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=600', title: 'Công viên xanh', desc: 'Không gian xanh 5000m²' },
        { img: 'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=600', title: 'Trung tâm thương mại', desc: 'Shopping mall hiện đại ngay chân tòa' },
        { img: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600', title: 'Phòng gym & Yoga', desc: 'Trang thiết bị cao cấp nhập khẩu' },
        { img: 'https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=600', title: 'Khu vui chơi trẻ em', desc: 'An toàn và sáng tạo' },
        { img: 'https://images.unsplash.com/photo-1557597774-9d273605dfa9?w=600', title: 'An ninh 24/7', desc: 'Hệ thống camera AI, bảo vệ chuyên nghiệp' },
    ];

    const layouts = [
        {
            type: 'Studio',
            area: '34-46m²',
            price: 'từ ~1,4 tỷ',
            color: 'bg-sky-400',
            img: 'https://res.cloudinary.com/dcoi0nc05/image/upload/v1767838684/C%C4%82N_H%E1%BB%98_STUDIO_2_xugde7.jpg'
        },
        {
            type: '1PN',
            area: '41-52m²',
            price: 'từ ~1,7 tỷ',
            color: 'bg-pink-400',
            img: 'https://images.unsplash.com/photo-1502672023488-70e25813eb80?w=800'
        },
        {
            type: '1PN+, 1WC',
            area: '53-62m²',
            price: 'từ ~2 tỷ',
            color: 'bg-orange-400',
            img: 'https://res.cloudinary.com/dcoi0nc05/image/upload/v1767838299/1PN_iysetd.jpg'
        },
        {
            type: '2PN, 1WC',
            area: '64-66m²',
            price: 'từ ~2,2 tỷ',
            color: 'bg-lime-400',
            img: 'https://images.unsplash.com/photo-1600210492493-0946911123ea?w=800'
        },
        {
            type: '2PN, 2WC',
            area: '64-66m²',
            price: 'từ ~2,3 tỷ',
            color: 'bg-yellow-400',
            img: 'https://res.cloudinary.com/dcoi0nc05/image/upload/v1767838846/PH%C3%92NG_KH%C3%81CH_2_laftcg.jpg'
        },
        {
            type: '3PN, 2WC',
            area: '83m²',
            price: 'từ ~2,9 tỷ',
            color: 'bg-purple-400',
            img: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=800'
        },
    ];



    return (
        <div className="min-h-screen bg-white overflow-x-hidden">
            {/* Navigation */}
            <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <div className="flex items-center space-x-2">
                            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-lg flex items-center justify-center">
                                <Sparkles className="w-6 h-6 text-white" />
                            </div>
                            <span
                                data-testid="nav-title"
                                className={`font-bold text-xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}
                            >
                                The Emerald Garden View
                            </span>
                        </div>

                        {/* Desktop Menu */}
                        <div className="hidden md:flex space-x-8">
                            {[
                                { id: 'home', label: 'Trang chủ', icon: Home },
                                { id: 'about', label: 'Giới thiệu', icon: Building2 },
                                { id: 'location', label: 'Vị trí', icon: MapPin },
                                { id: 'amenities', label: 'Tiện ích', icon: Sparkles },
                                { id: 'layouts', label: 'Mặt bằng', icon: Layout },
                                { id: 'contact', label: 'Liên hệ', icon: MessageCircle },
                            ].map(({ id, label, icon: Icon }) => (
                                <button
                                    key={id}
                                    onClick={() => scrollToSection(id)}
                                    className={`flex items-center space-x-1 transition-colors ${activeSection === id
                                        ? 'text-emerald-600 font-semibold'
                                        : isScrolled
                                            ? 'text-gray-700 hover:text-emerald-600'
                                            : 'text-white hover:text-emerald-300'
                                        }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    <span>{label}</span>
                                </button>
                            ))}
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="md:hidden p-2"
                        >
                            {mobileMenuOpen ? (
                                <X className={isScrolled ? 'text-gray-900' : 'text-white'} />
                            ) : (
                                <Menu className={isScrolled ? 'text-gray-900' : 'text-white'} />
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden bg-white border-t">
                        {[
                            { id: 'home', label: 'Trang chủ' },
                            { id: 'about', label: 'Giới thiệu' },
                            { id: 'location', label: 'Vị trí' },
                            { id: 'amenities', label: 'Tiện ích' },
                            { id: 'layouts', label: 'Mặt bằng' },
                            { id: 'contact', label: 'Liên hệ' },
                        ].map(({ id, label }) => (
                            <button
                                key={id}
                                onClick={() => scrollToSection(id)}
                                className="block w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors"
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                )}
            </nav>

            {/* Hero Section */}
            <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 via-teal-800 to-emerald-950">
                    <img
                        src="https://res.cloudinary.com/dcoi0nc05/image/upload/v1762997065/Emerald_C02_Facade_Night_Final_Rev02_ahjnnc.jpg?w=1920"
                        alt="Hero"
                        className="w-full h-full object-cover opacity-30"
                    />
                </div>
                <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
                    <div className="mb-6 inline-block">
                        <span className="px-6 py-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-gray-900 font-semibold rounded-full text-sm">
                            ✨ Dự án hot nhất Thuận An 2025
                        </span>
                    </div>
                    <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600">
                            The Emerald
                        </span>
                        <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-teal-300">
                            Garden View
                        </span>
                    </h1>
                    <p className="text-xl md:text-2xl text-emerald-100 mb-8 max-w-3xl mx-auto">
                        Không gian sống xanh - Tiện ích đẳng cấp - Vị trí vàng tại trung tâm Thuận An, Bình Dương
                    </p>
                    <button
                        onClick={() => scrollToSection('contact')}
                        className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-amber-500 text-gray-900 font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all duration-300 text-lg"
                    >
                        Đăng ký nhận thông tin
                    </button>
                </div>
                <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
                    <Compass className="w-8 h-8 text-white opacity-50" />
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="py-20 bg-gradient-to-b from-white to-emerald-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Giới thiệu dự án
                        </h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto"></div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <img
                                src="https://res.cloudinary.com/dcoi0nc05/image/upload/v1763002412/2025.11.10_PRINT_EGD_LEAFLET_compressed-images-0_v1_rpmij6.jpg?w=800"
                                alt="Project"
                                className="rounded-2xl shadow-2xl"
                            />
                        </div>
                        <div className="space-y-6">
                            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-emerald-500">
                                <h3 className="font-bold text-xl text-gray-900 mb-2">📍 Vị trí</h3>
                                <p className="text-gray-600">Mặt tiền đường Nguyễn Chí Thanh, P.Thuận An, TP.HCM</p>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-teal-500">
                                <h3 className="font-bold text-xl text-gray-900 mb-2">🏢 Quy mô</h3>
                                <p className="text-gray-600">2 tòa tháp 25 tầng, 2000 căn hộ cao cấp, diện tích 34m² - 83m²</p>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-yellow-500">
                                <h3 className="font-bold text-xl text-gray-900 mb-2">👔 Đơn vị phát triển</h3>
                                <p className="text-gray-600">Lê Phong Group - Uy tín 20 năm phát triển BĐS</p>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-amber-500">
                                <h3 className="font-bold text-xl text-gray-900 mb-2">📋 Pháp lý</h3>
                                <p className="text-gray-600">Sổ hồng lâu dài, bàn giao hoàn thiện nội thất cao cấp</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Location Section */}
            <section id="location" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Vị trí đắc địa
                        </h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto mb-6"></div>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Tọa lạc tại trung tâm Thuận An, kết nối hoàn hảo với toàn bộ khu vực
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 mb-12">
                        <div className="space-y-4">
                            <div className="flex items-start space-x-4 p-4 bg-emerald-50 rounded-xl">
                                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <MapPin className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-900 mb-1">KCN VSIP Bình Dương</h4>
                                    <p className="text-gray-600">Chỉ 5 phút di chuyển - Trung tâm công nghiệp lớn nhất khu vực</p>
                                </div>
                            </div>
                            <div className="flex items-start space-x-4 p-4 bg-teal-50 rounded-xl">
                                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <MapPin className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-900 mb-1">Trung tâm Thuận An</h4>
                                    <p className="text-gray-600">3 phút đến UBND, bệnh viện, trường học</p>
                                </div>
                            </div>
                            <div className="flex items-start space-x-4 p-4 bg-yellow-50 rounded-xl">
                                <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <MapPin className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-900 mb-1">TP.HCM</h4>
                                    <p className="text-gray-600">20 phút qua cầu Bình Triệu - Kết nối nhanh trung tâm Sài Gòn</p>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-2xl overflow-hidden shadow-2xl h-96">
                            <iframe
                                title="The Emerald Garden View - Location Map"
                                src="https://www.google.com/maps/embed?pb=!1m13!1m8!1m3!1d1265.9576598829822!2d106.69271057894103!3d10.939706442546706!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTDCsDU2JzMwLjIiTiAxMDbCsDQxJzM3LjMiRQ!5e1!3m2!1sen!2sus!4v1762999913793!5m2!1sen!2sus"
                                className="w-full h-full"
                                style={{ border: 0 }}
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            ></iframe>
                        </div>
                    </div>
                </div>
            </section>

            {/* Amenities Section */}
            <section id="amenities" className="py-20 bg-gradient-to-b from-emerald-50 to-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Tiện ích đẳng cấp
                        </h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto mb-6"></div>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Trải nghiệm cuộc sống resort 5 sao ngay tại nhà
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {amenities.map((item, idx) => (
                            <div
                                key={idx}
                                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 overflow-hidden"
                            >
                                <div className="relative h-48 overflow-hidden">
                                    <img
                                        src={item.img}
                                        alt={item.title}
                                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                                </div>
                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                                    <p className="text-gray-600">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Layouts Section */}
            <section id="layouts" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Mặt bằng & Căn hộ
                        </h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto mb-6"></div>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Đa dạng diện tích, linh hoạt thiết kế theo nhu cầu
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {layouts.map((layout, idx) => (
                            <div key={idx} className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                                <div className="relative h-64 overflow-hidden">
                                    <img
                                        src={layout.img}
                                        alt={layout.type}
                                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                                    />
                                    <div className="absolute top-4 right-4 px-4 py-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-gray-900 font-bold rounded-full">
                                        {layout.price}
                                    </div>
                                </div>
                                <div className="p-6">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{layout.type}</h3>
                                    <p className="text-gray-600 text-lg mb-1">{layout.area}</p>
                                    <p className="text-emerald-600 font-bold text-xl mb-4">{layout.price}</p>
                                    <button
                                        onClick={() => scrollToSection('contact')}
                                        className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all"
                                    >
                                        Xem chi tiết
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-12 md:py-20 bg-gradient-to-b from-emerald-900 to-teal-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 md:mb-16">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                            Đăng ký nhận thông tin
                        </h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-amber-500 mx-auto mb-4 md:mb-6"></div>
                        <p className="text-lg md:text-xl text-emerald-100 max-w-3xl mx-auto px-4">
                            Để lại thông tin để nhận tư vấn và ưu đãi đặc biệt
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 md:gap-12">
                        <div className="space-y-4 md:space-y-6">
                            <div className="flex items-center space-x-4 p-4 md:p-6 bg-white/10 rounded-xl backdrop-blur">
                                <Phone className="w-6 h-6 md:w-8 md:h-8 text-yellow-400 flex-shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs md:text-sm text-emerald-200">Hotline</p>
                                    <p className="text-lg md:text-xl font-bold break-all">0794 402 537</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4 p-4 md:p-6 bg-white/10 rounded-xl backdrop-blur">
                                <Mail className="w-6 h-6 md:w-8 md:h-8 text-yellow-400 flex-shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs md:text-sm text-emerald-200">Email</p>
                                    <p className="text-sm md:text-xl font-bold break-all">emeraldgardenview@gmail.com</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4 p-4 md:p-6 bg-white/10 rounded-xl backdrop-blur">
                                <MapPin className="w-6 h-6 md:w-8 md:h-8 text-yellow-400 flex-shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs md:text-sm text-emerald-200">Địa chỉ</p>
                                    <p className="text-sm md:text-xl font-bold">Nguyễn Chí Thanh, P.Thuận An, TP.HCM</p>
                                </div>
                            </div>
                        </div>

                        <ContactForm context="Đăng ký nhận thông tin" />
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="flex items-center justify-center space-x-2 mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-lg flex items-center justify-center">
                            <Sparkles className="w-6 h-6 text-white" />
                        </div>
                        <span className="font-bold text-xl">The Emerald Garden View</span>
                    </div>
                    <p className="text-gray-400 mb-4">
                        Dự án căn hộ cao cấp tại trung tâm Thuận An, Bình Dương
                    </p>
                    <div className="flex flex-col items-center gap-2 text-sm text-gray-400 md:flex-row md:justify-center md:gap-6">
                        <span>© 2025 Emerald Group</span>
                        <span className="hidden md:inline">•</span>
                        <span>Hotline: 0789 153 295</span>
                        <span className="hidden md:inline">•</span>
                        <span>emeraldgardenview@gmail.com</span>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default EmeraldGardenWebsite;